// === WT4 finance (§12): блок LTV клиента на карточке заявки ===
// GET /api/v1/parties/client-account/{id}/ltv/ — показатели раздельно по
// валютам (без смешивания), фильтр периода, drill-down по заявкам/операциям.
import { useState } from 'react';
import { parties } from '../../api/endpoints';
import type { ClientLtv, ClientLtvOperation } from '../../api/types';
import {
    Card, Column, DataTable, ErrorBox, Field, fmtDate, fmtMoney, shortId,
    StatusBadge,
} from '../../components/ui';
import { useAsync } from '../../hooks';

function MoneyMap({ map, empty = '—' }: { map: Record<string, string> | undefined; empty?: string }) {
    const entries = Object.entries(map ?? {});
    if (!entries.length) return <span className="muted">{empty}</span>;
    return (
        <span>
            {entries.map(([cur, value]) => (
                <span key={cur} style={{ display: 'inline-block', marginRight: 12 }}>
                    {fmtMoney(value, cur)}
                </span>
            ))}
        </span>
    );
}

export function ClientLtvCard({ accountId }: { accountId?: string | null }) {
    const [periodFrom, setPeriodFrom] = useState('');
    const [periodTo, setPeriodTo] = useState('');

    const ltv = useAsync<ClientLtv>(
        () => accountId
            ? parties.clientAccountLtv(accountId, {
                period_from: periodFrom || undefined,
                period_to: periodTo || undefined,
            })
            : Promise.reject(new Error('no account')),
        [accountId, periodFrom, periodTo],
    );

    if (!accountId) return null;
    const data = ltv.data;

    const requestCols: Column<NonNullable<ClientLtv['requests']>[number]>[] = [
        { key: 'number', title: 'Заявка', render: (r) => (
            <a href={`#/requests/${r.id}`}>{r.number ?? shortId(r.id)}</a>
        ) },
        { key: 'stage', title: 'Стадия', render: (r) => <StatusBadge value={r.stage} /> },
        { key: 'success', title: 'Успешная', render: (r) => (r.success ? 'Да' : '—') },
        { key: 'agreed', title: 'Согласованные продажи', render: (r) => <MoneyMap map={r.agreed_sales} /> },
        { key: 'paid', title: 'Оплачено нетто', render: (r) => <MoneyMap map={r.payments_net} /> },
    ];

    type OpRow = ClientLtvOperation & { id?: string };
    const opCols: Column<OpRow>[] = [
        { key: 'date', title: 'Дата', render: (r) => fmtDate(r.date) },
        { key: 'payment', title: 'Платёж', render: (r) => r.payment_external_id || shortId(r.payment_id) },
        { key: 'invoice', title: 'Счёт', render: (r) => r.invoice_number ?? shortId(r.invoice_id) },
        { key: 'request', title: 'Заявка', render: (r) => (
            <a href={`#/requests/${r.request_id}`}>{shortId(r.request_id)}</a>
        ) },
        { key: 'amount', title: 'Распределено', render: (r) => fmtMoney(r.amount, r.currency) },
        { key: 'reversed', title: 'Возврат', render: (r) => fmtMoney(r.reversed, r.currency) },
        { key: 'net', title: 'Нетто', render: (r) => fmtMoney(r.net, r.currency) },
    ];

    return (
        <Card title="LTV клиента" actions={
            <div className="form-actions" style={{ margin: 0 }}>
                <Field label="Период с">
                    <input className="input" type="date" value={periodFrom}
                           onChange={(e) => setPeriodFrom(e.target.value)} />
                </Field>
                <Field label="по">
                    <input className="input" type="date" value={periodTo}
                           onChange={(e) => setPeriodTo(e.target.value)} />
                </Field>
                <button className="btn small" onClick={ltv.reload}>Обновить</button>
            </div>
        }>
            <ErrorBox error={ltv.error} onRetry={ltv.reload} />
            {ltv.loading && !data ? <div className="muted">Загрузка LTV…</div> : null}
            {data ? (
                <>
                    {data.partial ? (
                        <div className="alert alert-warn">
                            Показаны только доступные вам заявки клиента — сводка неполная.
                        </div>
                    ) : null}
                    <div className="kv-grid">
                        <div className="kv"><span className="kv-key">Согласованные продажи</span>
                            <span className="kv-value"><MoneyMap map={data.agreed_sales} /></span></div>
                        <div className="kv"><span className="kv-key">Оплачено (нетто возвратов)</span>
                            <span className="kv-value"><MoneyMap map={data.payments_net} /></span></div>
                        <div className="kv"><span className="kv-key">Средний чек</span>
                            <span className="kv-value"><MoneyMap map={data.avg_check} /></span></div>
                        <div className="kv"><span className="kv-key">Успешных заявок</span>
                            <span className="kv-value">{data.requests_success_count}</span></div>
                    </div>
                    {data.avg_check_basis ? (
                        <div className="field-hint">Средний чек: {data.avg_check_basis}</div>
                    ) : null}
                    <DataTable columns={requestCols} rows={data.requests ?? []}
                               empty="Заявок клиента в периоде нет" />
                    {data.operations?.length ? (
                        <div className="section" style={{ marginTop: 8 }}>
                            <h3>Операции (drill-down)</h3>
                            <DataTable columns={opCols}
                                       rows={data.operations.map((o) => ({ ...o, id: o.allocation_id }))} />
                        </div>
                    ) : null}
                </>
            ) : null}
        </Card>
    );
}
